import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select, { StylesConfig } from 'react-select';

import { brandColors } from '@/app/constants';
import CircleWarningIcon from '@/component-library/primitives/Icons/CircleWarningIcon/CircleWarningIcon';

type MultiValueChipColor = 'green' | 'red';
interface SelectFieldProps {
  name: string;
  label?: string | React.ReactElement;
  control: any;
  options:
    | { label: string; value: string }[]
    | { label?: string; options: { label: string; value: string }[] }[]
    | undefined;
  errorMsg: string | undefined;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isSearchable?: boolean;
  isMulti?: true | undefined;
  multiValueChipColor?: MultiValueChipColor;
  withBorder?: boolean;
  customOnChange?: (...args: any[]) => void;
  value?: any;
  isClearable?: boolean;
}

export interface OptionType {
  label: string;
  value: string;
}

const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  control,
  options,
  errorMsg,
  placeholder,
  className,
  disabled = false,
  isSearchable = true,
  isMulti = undefined,
  withBorder = false,
  multiValueChipColor = 'green',
  customOnChange,
  value,
  isClearable = false,
}) => {
  const { t } = useTranslation();
  const backgroundColor = brandColors.silver;

  const customSelectStyles: StylesConfig<OptionType, true> = {
    control: (baseStyles) => ({
      ...baseStyles,
      borderColor: errorMsg ? '#D81212' : 'transparent',
      backgroundColor: `${backgroundColor}`,
      boxShadow: 'none',
      '&:hover': {
        outline: 'none',
        borderColor: 'transparent',
      },
      cursor: 'pointer',
      borderRadius: '0.5rem',
      minHeight: '3rem',
      color: 'yellow',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: `${backgroundColor}`,
      minWidth: 'fit-content',
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: `${backgroundColor}`,
      borderRadius: '0.5rem',
      '::-webkit-scrollbar': {
        width: '4px',
        height: '0px',
      },
      '::-webkit-scrollbar-track': {
        background: '#ffffffff',
      },
      '::-webkit-scrollbar-thumb': {
        background: '#888',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: '#555',
      },
    }),
    option: (baseStyles, { isSelected }) => ({
      ...baseStyles,
      backgroundColor: 'transparent',
      color: isSelected ? brandColors.black : brandColors.darkSilver,
      '&:hover': {
        color: brandColors.black,
      },
      '&:active': {
        backgroundColor: 'transparent',
      },
      cursor: 'pointer',
      fontWeight: isSelected ? '600' : '400',
      fontSize: '0.875rem',
      borderRadius: '0.5rem',
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
      marginBottom: '2px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#707070ff',
      fontSize: '0.875rem',
      paddingLeft: '2px',
    }),
    placeholder: (defaultStyles) => {
      return {
        ...defaultStyles,
        color: brandColors.darkSilver,
        fontWeight: '400',
        fontSize: '0.875rem',
        paddingLeft: '2px',
      };
    },
    dropdownIndicator: (provided) => ({
      ...provided,
      color: brandColors.darkSilver,
      paddingRight: '16px',
      '&:hover': {
        color: brandColors.darkSilver,
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: brandColors.darkSilver,
      '&:hover': {
        color: brandColors.darkSilver,
      },
    }),
    input: (base) => ({
      ...base,
      color: '#f5f7fc',
      fontWeight: '400',
      fontSize: '0.875rem',
    }),
    multiValue: (baseStyles) => ({
      ...baseStyles,
      color: '#181A1E',
      backgroundColor: multiValueChipColor === 'green' ? '#82E6B8' : '#ff9296',
      padding: '4px',
      borderRadius: '0,5rem',
      fontWeight: '500',
    }),
    multiValueRemove: (baseStyles) => ({
      ...baseStyles,
      color: '#1C222A',
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'white',
      },
      '&>svg': {
        width: '18px',
        height: '18px',
        stroke: multiValueChipColor === 'green' ? '#82E6B8' : '#ff9296',
        strokeWidth: '1px',
      },
    }),
  };

  const NoOptionsMessage = () => {
    return (
      <div className="flex flex-col gap-4 items-center justify-center w-full h-full mt-6 mb-6">
        <h2 className="text-custom-darkSilver text-center text-xl font-semibold">{t('SelectField.noResultsLabel')}</h2>
        <p className="text-custom-darkSilver text-sm font-normal tracking-[0.28px] text-center">
          {t('SelectField.noResultsMsg')}
        </p>
      </div>
    );
  };

  return (
    <div className={`${disabled && 'opacity-50 pointer-events-none'}`}>
      {label && (
        <label
          className={`block text-sm mb-1 font-medium
                ${errorMsg ? 'text-custom-red' : 'text-custom-darkSilver'}
                ${disabled && 'opacity-50'}
              `}
        >
          {label}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur, value: controllerValue, name, ref } }) => (
          <>
            <Select
              onBlur={onBlur}
              name={name}
              ref={ref}
              value={value !== undefined ? value : controllerValue}
              onChange={(e, action) => {
                onChange(e);
                customOnChange && customOnChange(e, action);
              }}
              className={className}
              styles={customSelectStyles}
              options={options}
              isSearchable={isSearchable}
              components={{
                IndicatorSeparator: () => null,
                NoOptionsMessage,
              }}
              isMulti={isMulti}
              isClearable={isClearable}
              placeholder={<>{placeholder ? placeholder : t('SelectField.selectPlaceholder')}</>}
            />
            {errorMsg && (
              <p className="text-custom-red mt-1 text-xs font-medium flex flex-row items-center gap-1">
                <CircleWarningIcon /> {errorMsg}
              </p>
            )}
          </>
        )}
      />
    </div>
  );
};

export default SelectField;
